// Download / pull (SPEC §5): ref → commit → recursive tree → changed .md blobs.
// Never overwrites local work: files with local edits or in unpushed commits are
// left alone and reported (block-level merge and the conflict screen come in M7).
import type { GitHub } from '../github/client';
import { gitBlobShaOfText } from './blobSha';
import { basePath, type Folder } from './folder';
import { decodeRaw, detectEncoding, isTrackedPath, normalizePath, normalizeText } from './normalize';
import { writeState, type WorkState } from './state';

export type PullProgress = { done: number; total: number };
export type PullResult = {
  headSha: string;
  /** Written or updated from the remote. */
  updated: string[];
  /** Removed locally because they were deleted on the remote and unchanged here. */
  deleted: string[];
  /** Changed on both sides, or a local file in the way: left untouched for conflict handling. */
  kept: string[];
};

type TreeEntry = { path: string; type: 'blob' | 'tree' | 'commit'; sha: string; mode: string };

export class PullError extends Error {
  constructor(message: string) { super(message); this.name = 'PullError'; }
}

const CONCURRENCY = 6;

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64.replace(/\s/g, ''));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function pull(opts: {
  gh: GitHub;
  folder: Folder;
  repo: { owner: string; name: string };
  state: WorkState;
  onProgress?: (p: PullProgress) => void;
}): Promise<{ result: PullResult; state: WorkState }> {
  const { gh, folder, state } = opts;
  const R = `/repos/${encodeURIComponent(opts.repo.owner)}/${encodeURIComponent(opts.repo.name)}`;

  const ref = await gh.rest<{ object: { sha: string } }>(`${R}/git/ref/heads/${state.branch.split('/').map(encodeURIComponent).join('/')}`);
  if (!ref) throw new PullError(`Branch ${state.branch} doesn't exist on GitHub.`);
  const headSha = ref.object.sha;
  const commit = await gh.rest<{ tree: { sha: string } }>(`${R}/git/commits/${headSha}`);
  if (!commit) throw new PullError(`Commit ${headSha} not found.`);
  const treeSha = commit.tree.sha;
  const tree = await gh.rest<{ tree: TreeEntry[]; truncated: boolean }>(`${R}/git/trees/${treeSha}?recursive=1`);
  if (!tree) throw new PullError(`Tree ${treeSha} not found.`);
  if (tree.truncated) throw new PullError('The repository is too large to download in one request.');

  const remote = new Map<string, string>(); // NFC path → blob sha
  for (const e of tree.tree) {
    const p = normalizePath(e.path);
    if (e.type === 'blob' && isTrackedPath(p)) remote.set(p, e.sha);
  }

  // Files in unpushed commits are local work even if the working file matches.
  const committed = new Set(state.localCommits.flatMap((c) => c.changes.map((ch) => ch.path)));
  const files = { ...state.files };
  const result: PullResult = { headSha, updated: [], deleted: [], kept: [] };

  const localSha = async (path: string) => {
    const text = await folder.readText(path);
    return text === null ? null : gitBlobShaOfText(text);
  };

  // Decide what to fetch.
  const toFetch: [string, string][] = [];
  for (const [path, sha] of remote) {
    const prev = files[path];
    if (prev && prev.blobSha === sha) continue; // remote unchanged
    toFetch.push([path, sha]);
  }

  let done = 0;
  const total = toFetch.length;
  opts.onProgress?.({ done, total });

  const fetchOne = async ([path, sha]: [string, string]) => {
    const prev = files[path];
    const current = await localSha(path);
    const blob = await gh.rest<{ content: string; encoding: string }>(`${R}/git/blobs/${sha}`);
    if (!blob || blob.encoding !== 'base64') throw new PullError(`Couldn't download ${path}.`);
    const raw = decodeRaw(base64ToBytes(blob.content));
    const enc = detectEncoding(raw);
    const text = normalizeText(raw);
    const newLocalSha = await gitBlobShaOfText(text);

    if (current === newLocalSha) {
      // Already identical locally (e.g. a resumed download): just record it.
      await folder.writeText(basePath(path), text);
      files[path] = { blobSha: sha, localSha: newLocalSha, ...enc };
    } else {
      const locallyChanged = prev ? current !== prev.localSha : current !== null;
      if (locallyChanged || committed.has(path)) {
        result.kept.push(path); // both sides changed: never overwrite (conflicts: M7)
      } else {
        await folder.writeText(path, text);
        await folder.writeText(basePath(path), text);
        files[path] = { blobSha: sha, localSha: newLocalSha, ...enc };
        result.updated.push(path);
      }
    }
    opts.onProgress?.({ done: ++done, total });
  };

  const queue = [...toFetch];
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
    for (let item = queue.shift(); item; item = queue.shift()) await fetchOne(item);
  }));

  // Deleted on the remote.
  for (const path of Object.keys(files)) {
    if (remote.has(path)) continue;
    const current = await localSha(path);
    if (current === null) { delete files[path]; await folder.remove(basePath(path)); continue; }
    if (current === files[path]!.localSha && !committed.has(path)) {
      await folder.remove(path);
      await folder.remove(basePath(path));
      delete files[path];
      result.deleted.push(path);
    } else {
      result.kept.push(path);
    }
  }

  // Only advance the remote head when everything is in sync; kept files still need
  // their old base for the three-way merge (M7), and push must not skip past them.
  result.kept.sort();
  const next: WorkState = result.kept.length
    ? { ...state, files, needsResolving: result.kept }
    : { ...state, files, remoteHeadSha: headSha, remoteTreeSha: treeSha, needsResolving: [] };
  await writeState(folder, next);
  result.updated.sort(); result.deleted.sort(); result.kept.sort();
  return { result, state: next };
}
